import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  ArrowRight,
  Droplets,
  Calendar,
  Snowflake,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const packages = [
  {
    name: 'Complete Irrigation System Care Program',
    description: 'Comprehensive year-round care for optimal system performance',
    featured: true,
    features: [
      { text: 'Spring Startup', main: true },
      { text: 'Three In-Season System Inspections', main: true },
      { text: 'Full system run-through', sub: true },
      { text: 'Head and nozzle adjustments', sub: true },
      { text: 'Leak checks', sub: true },
      { text: 'Coverage corrections', sub: true },
      { text: 'Seasonal water schedule adjustments as needed', sub: true },
      { text: 'Winterization and blowout', main: true },
      { text: 'Priority 48-hour response', main: true }
    ],
    pricing: [
      { zones: '1-6 zones', price: '$575' },
      { zones: '7-10 zones', price: '$695' },
      { zones: '11-15 zones', price: '$850' },
      { zones: '16+ zones', price: 'Custom pricing' }
    ]
  },
  {
    name: 'Essential Irrigation Care Program',
    description: 'Core seasonal services for reliable system maintenance',
    featured: false,
    features: [
      { text: 'Spring Startup', main: true },
      { text: 'One Mid-Season Inspection (July)', main: true },
      { text: 'Winterization', main: true },
      { text: 'Priority scheduling (non-emergency)', main: true }
    ],
    pricing: [
      { zones: '1-6 zones', price: '$450' },
      { zones: '7-10 zones', price: '$525' },
      { zones: '11-15 zones', price: '$650' },
      { zones: '16+ zones', price: 'Custom pricing' }
    ]
  }
];

const Packages = () => {
  return (
    <div className="pt-24 md:pt-28" data-testid="packages-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-mist-dark" data-testid="packages-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              Seasonal Care Programs
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-mist-white mb-6">
              Irrigation <span className="text-mist-light">Packages</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-light/80 max-w-2xl mx-auto">
              Choose the maintenance program that fits your needs. Both packages are designed 
              to keep your irrigation system performing efficiently season after season.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="packages-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="h-full"
              >
                <Card 
                  className={`h-full border-2 rounded-3xl overflow-hidden ${
                    pkg.featured 
                      ? 'border-landscape-green shadow-2xl' 
                      : 'border-mist-light shadow-lg'
                  }`}
                  data-testid={`package-card-${index}`}
                >
                  {/* Badge area - same height for both cards */}
                  {pkg.featured ? (
                    <div className="bg-landscape-green text-white text-center py-2 font-body text-sm font-bold uppercase tracking-wider">
                      Most Popular
                    </div>
                  ) : (
                    <div className="bg-mist-blue text-mist-blue text-center py-2 font-body text-sm font-bold uppercase tracking-wider">
                      &nbsp;
                    </div>
                  )}
                  <CardContent className="p-8 md:p-10 flex flex-col h-full">
                    {/* Package Header */}
                    <div className="mb-8">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                        pkg.featured ? 'bg-landscape-green' : 'bg-mist-light'
                      }`}>
                        {pkg.featured ? (
                          <Calendar className="w-7 h-7 text-white" />
                        ) : (
                          <Droplets className="w-7 h-7 text-mist-blue" />
                        )}
                      </div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-mist-dark mb-3">
                        {pkg.name}
                      </h2>
                      <p className="font-body text-mist-blue">
                        {pkg.description}
                      </p>
                    </div>

                    {/* Features List */}
                    <div className="mb-8 flex-grow">
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-4">
                        What's Included:
                      </h3>
                      <ul className="space-y-3">
                        {pkg.features.map((feature, fIndex) => (
                          <li 
                            key={fIndex} 
                            className={`flex items-start gap-3 ${feature.sub ? 'ml-6' : ''}`}
                          >
                            <CheckCircle2 
                              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                feature.sub 
                                  ? 'text-mist-blue/60' 
                                  : pkg.featured 
                                    ? 'text-landscape-green' 
                                    : 'text-mist-blue'
                              }`} 
                            />
                            <span className={`font-body ${
                              feature.sub 
                                ? 'text-mist-blue/80 text-sm' 
                                : 'text-mist-dark font-medium'
                            }`}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Pricing and CTA - Always at bottom */}
                    <div className="mt-auto">
                      {/* Pricing */}
                      <div className={`rounded-2xl p-6 ${
                        pkg.featured ? 'bg-landscape-green/10' : 'bg-mist-light'
                      }`}>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-4">
                        Pricing:
                      </h3>
                      <div className="space-y-2">
                        {pkg.pricing.map((price, pIndex) => (
                          <div 
                            key={pIndex} 
                            className="flex justify-between items-center"
                          >
                            <span className="font-body text-mist-blue">
                              {price.zones}
                            </span>
                            <span className={`font-heading font-bold ${
                              pkg.featured ? 'text-landscape-green' : 'text-mist-dark'
                            }`}>
                              {price.price}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                      <Link to="/contact" className="mt-8 block">
                        <Button 
                          className={`w-full rounded-full h-14 text-lg font-medium ${
                            pkg.featured 
                              ? 'bg-landscape-green hover:bg-landscape-dark text-white' 
                              : 'bg-mist-blue hover:bg-mist-dark text-white'
                          }`}
                          data-testid={`package-cta-${index}`}
                        >
                          Get Started
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <motion.div {...fadeUp} className="mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-mist-light rounded-2xl">
              <Clock className="w-6 h-6 text-mist-blue" />
              <span className="font-body text-mist-dark">
                All packages include <strong>priority scheduling</strong> for enrolled customers
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose a Package */}
      <section className="py-24 md:py-32 bg-mist-light" data-testid="why-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-mist-blue/20 text-mist-blue font-body text-sm font-bold uppercase tracking-widest rounded-full mb-4">
              Benefits
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-mist-dark mb-6">
              Why Choose a Seasonal Care Program?
            </h2>
            <p className="font-body text-lg text-mist-blue max-w-2xl mx-auto">
              Proactive maintenance saves money and extends the life of your irrigation system.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Droplets,
                title: 'Prevent Costly Repairs',
                desc: 'Regular inspections catch small issues before they become expensive problems.'
              },
              {
                icon: Calendar,
                title: 'Season-Ready System',
                desc: 'Your system is properly started in spring and protected for winter.'
              },
              {
                icon: Snowflake,
                title: 'Winterization Protection',
                desc: 'Professional blowout service prevents freeze damage to pipes and heads.'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-white border-0 shadow-lg rounded-2xl h-full" data-testid={`benefit-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 bg-mist-light rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <benefit.icon className="w-7 h-7 text-mist-blue" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-mist-dark mb-3">
                      {benefit.title}
                    </h3>
                    <p className="font-body text-mist-blue">
                      {benefit.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-landscape-green" data-testid="packages-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Ready to Protect Your Investment?
            </h2>
            <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Contact us today to enroll in a Seasonal Care Program. We'll help you choose 
              the right package for your property and schedule your first service.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="cta-contact-btn"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:7242945999">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-landscape-green rounded-full px-8 py-6 text-lg font-medium"
                  data-testid="cta-call-btn"
                >
                  Call (724) 294-5999
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
